// NutritionCalculatorForm.jsx
import React, { useState, useEffect, useRef } from "react";
import apiService from "../../../ApiService"; // adjust the import path as needed
import { API_ENDPOINTS } from "../../../ApiService";
import { MainContainer } from "../../pages/styles/sharedStyles";
import Title from "../title/Title";
import Section from "../section/Section";
import { tokens } from "../../../theme";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  TextField,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import InfoTextFieldWithDialog from "../tooltip/infoTextFieldWithDialog/InfoTextFieldWithDialog";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MacroPieChart from "../charts/pieChart/MacroPieChart";

const NutritionCalculatorForm = ({
  showClientSelection = true,
  initialNutritionData = {},
  onSaveSuccess,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [nutritionData, setNutritionData] = useState({
    poidsActuel: "",
    fatPercentage: "",
    physicalActivityFactor: "",
    thermicEffectOfFoodFactor: 1,
    weightTrainingDuration: "",
    energyBalanceFactor: 1,
    trainingDaysPerWeek: "",
    repartitionFat: "",
    clientid: "",
    ...initialNutritionData,
  });
  const [calculatedData, setCalculatedData] = useState({
    leanMass: 0,
    bmr: 0,
    trainingEE: 0,
    REE: 0,
    totalEE: 0,
    maintenanceIntake: 0,
    targetIntake: 0,
    proteinGrams: 0,
    fatGrams: 0,
    carbsGrams: 0,
    proteinKcal: 0,
    fatKcal: 0,
    carbsKcal: 0,
  });

  const debounceTimeout = useRef(null);

  // If the form needs to show client selection, fetch clients.
  useEffect(() => {
    if (showClientSelection) {
      const fetchClientsWithoutNutrition = async () => {
        try {
          const response = await apiService.get(
            API_ENDPOINTS.FETCH_CLIENT_WITHOUT_NUTRITION
          );
          setClients(response.data);
        } catch (error) {
          console.error("Error fetching clients:", error);
        }
      };
      fetchClientsWithoutNutrition();
    }
  }, [showClientSelection]);

  // When a client is selected, pre-populate fields
  useEffect(() => {
    if (selectedClient) {
      const baseNutritionData = {
        poidsActuel: selectedClient.poidsactuel,
        fatPercentage: selectedClient.pourcentageMasseGrasse,
        thermicEffectOfFoodFactor: 1,
        weightTrainingDuration: 60,
        energyBalanceFactor: 1,
        trainingDaysPerWeek: selectedClient.nbjourdeworkout,
        clientid: selectedClient.clientID,
      };

      const physicalActivityPayload = {
        gender: selectedClient.gender,
        activityLevelCode: parseInt(selectedClient.activitylevel, 10),
      };

      apiService
        .post(API_ENDPOINTS.PHYSICAL_ACTIVITY_FACTOR, physicalActivityPayload)
        .then((response) => {
          baseNutritionData.physicalActivityFactor = response.data;
          setNutritionData(baseNutritionData);
        })
        .catch((error) => {
          console.error("Error fetching physical activity factor:", error);
          baseNutritionData.physicalActivityFactor = 1.0;
          setNutritionData(baseNutritionData);
        });
    }
  }, [selectedClient]);

  // Update nutritionData when a field changes
  const handleInputChange = (field, value) => {
    setNutritionData((prev) => ({ ...prev, [field]: value }));
  };

  // Recalculate nutrition with debounce
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    const { poidsActuel, fatPercentage, weightTrainingDuration } =
      nutritionData;
    if (poidsActuel && fatPercentage && weightTrainingDuration) {
      debounceTimeout.current = setTimeout(() => {
        calculateNutrition();
      }, 500);
    }
  }, [nutritionData]);

  const calculateNutrition = async () => {
    const parsedData = {
      ...nutritionData,
      poidsActuel: parseFloat(nutritionData.poidsActuel) || 0,
      fatPercentage: parseFloat(nutritionData.fatPercentage) || 0,
      physicalActivityFactor:
        parseFloat(nutritionData.physicalActivityFactor) || 0,
      thermicEffectOfFoodFactor:
        parseFloat(nutritionData.thermicEffectOfFoodFactor) || 0,
      weightTrainingDuration:
        parseFloat(nutritionData.weightTrainingDuration) || 0,
      energyBalanceFactor: parseFloat(nutritionData.energyBalanceFactor) || 0,
      trainingDaysPerWeek: parseFloat(nutritionData.trainingDaysPerWeek) || 0,
      repartitionFat: parseFloat(nutritionData.repartitionFat) || 0,
    };
    try {
      const response = await apiService.post(
        API_ENDPOINTS.NUTRITION_CALCULATION,
        nutritionData
      );
      console.log(response);
      setCalculatedData({
        leanMass: response.data.leanMass ?? 0,
        bmr: response.data.bmr ?? 0,
        trainingEE: response.data.trainingEE ?? 0,
        REE: response.data.ree ?? 0,
        totalEE: response.data.totalEE ?? 0,
        maintenanceIntake: response.data.maintenanceIntake ?? 0,
        targetIntake: response.data.targetIntake ?? 0,
        proteinGrams: response.data.proteinGrams ?? 0,
        fatGrams: response.data.fatGrams ?? 0,
        carbsGrams: response.data.carbsGrams ?? 0,
        proteinKcal: response.data.proteinKcal ?? 0,
        fatKcal: response.data.fatKcal ?? 0,
        carbsKcal: response.data.carbsKcal ?? 0,
      });
    } catch (error) {
      console.error("Error calculating nutrition:", error);
    }
  };

  const handleSave = async () => {
    const payload = {
      clientid: nutritionData.clientid,
      datenutrition: new Date(),
      apportcalorique: Math.round(calculatedData.targetIntake),
      proteines: Math.round(calculatedData.proteinGrams),
      glucides: Math.round(calculatedData.carbsGrams),
      lipides: Math.round(calculatedData.fatGrams),
      repartitionfat: Math.round(nutritionData.fatPercentage),
    };

    try {
      await apiService.post(
        API_ENDPOINTS.ADD_CLIENT_INFORMATION_NUTRITION,
        payload
      );
      alert("Nutrition program saved");
      if (onSaveSuccess) onSaveSuccess();
    } catch (error) {
      console.error("Error saving nutrition program:", error);
    }
  };

  // Build data for the macro pie chart
  const dataNutrition = [
    { id: "Protein", label: "Protein", value: calculatedData.proteinGrams },
    { id: "Fat", label: "Fat", value: calculatedData.fatGrams },
    { id: "Carbs", label: "Carbs", value: calculatedData.carbsGrams },
  ];

  return (
    <MainContainer>
      <Title
        title="Nutrition Calculator"
        summary="Calculate macros for a client or for advice"
      />
      <Section>
        <Paper
          elevation={3}
          sx={{ p: 1.5, mb: 2, backgroundColor: colors.primary[400] }}
        >
          <Typography variant="body1" color={colors.grey[100]} align="left">
            Enter the details to calculate the macros.
          </Typography>
        </Paper>
      </Section>
      <Section>
        <Box
          sx={{
            my: isMobile ? 1 : 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          {showClientSelection && (
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => `${option.prenom} ${option.nom}`}
              onChange={(event, newValue) => setSelectedClient(newValue)}
              isOptionEqualToValue={(option, value) =>
                option.clientID === value.clientID
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Client"
                  variant="outlined"
                />
              )}
              sx={{ width: isMobile ? "100%" : 400, mb: 2 }}
            />
          )}
          <Paper elevation={3} sx={{ p: 2 }}>
            {showClientSelection && selectedClient && (
              <Typography variant="h6" gutterBottom>
                Client: {selectedClient.prenom} {selectedClient.nom}
              </Typography>
            )}
            <Grid
              container
              spacing={2}
              direction={isMobile ? "column" : "row"}
              alignItems="stretch"
            >
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Input Values
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InfoTextFieldWithDialog
                      label="Body Weight (kg)"
                      value={nutritionData.poidsActuel ?? ""}
                      onChange={(e) =>
                        handleInputChange(
                          "poidsActuel",
                          parseFloat(e.target.value)
                        )
                      }
                      infoTitle="Body Weight"
                      infoDescription="Enter the current body weight in kilograms."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoTextFieldWithDialog
                      label="Fat Percentage (%)"
                      value={nutritionData.fatPercentage ?? ""}
                      onChange={(e) =>
                        handleInputChange(
                          "fatPercentage",
                          parseFloat(e.target.value)
                        )
                      }
                      infoTitle="Fat Percentage"
                      infoDescription="Enter the body fat percentage (e.g., 20 for 20%)."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoTextFieldWithDialog
                      label="Physical Activity Factor"
                      value={nutritionData.physicalActivityFactor ?? ""}
                      onChange={(e) =>
                        handleInputChange(
                          "physicalActivityFactor",
                          e.target.value
                        )
                      }
                      infoTitle="Physical Activity Factor"
                      infoDescription="Ranges from 1 - 1.5 depending on biological sex and lifestyle."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoTextFieldWithDialog
                      label="Thermic Effect of Food Factor"
                      value={nutritionData.thermicEffectOfFoodFactor ?? ""}
                      onChange={(e) =>
                        handleInputChange(
                          "thermicEffectOfFoodFactor",
                          e.target.value
                        )
                      }
                      infoTitle="Thermic Effect of Food Factor"
                      infoDescription={
                        <>
                          Ranges from 1.1 - 1.25 depending on diet quality and
                          body fat percentage.
                          <br />
                          Often assumed to be a constant 10%.
                        </>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoTextFieldWithDialog
                      label="Weight Training Duration (hours)"
                      value={nutritionData.weightTrainingDuration ?? ""}
                      onChange={(e) =>
                        handleInputChange(
                          "weightTrainingDuration",
                          parseFloat(e.target.value)
                        )
                      }
                      infoTitle="Weight Training Duration"
                      infoDescription="Enter the duration (in hours) of your weight training sessions."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoTextFieldWithDialog
                      label="Energy Balance Factor"
                      value={nutritionData.energyBalanceFactor ?? ""}
                      onChange={(e) =>
                        handleInputChange("energyBalanceFactor", e.target.value)
                      }
                      infoTitle="Energy Balance Factor"
                      infoDescription={
                        <>
                          A 5% energy surplus gives 1.05.
                          <br />A 20% deficit gives 0.8.
                        </>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoTextFieldWithDialog
                      label="Training Days Per Week"
                      value={nutritionData.trainingDaysPerWeek ?? ""}
                      onChange={(e) =>
                        handleInputChange(
                          "trainingDaysPerWeek",
                          parseInt(e.target.value, 10)
                        )
                      }
                      infoTitle="Training Days Per Week"
                      infoDescription="Enter the number of days you train per week."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoTextFieldWithDialog
                      label="Macro Fat %"
                      value={nutritionData.repartitionFat ?? ""}
                      onChange={(e) =>
                        handleInputChange(
                          "repartitionFat",
                          parseFloat(e.target.value)
                        )
                      }
                      infoTitle="Fat % for Macro Calculation"
                      infoDescription={
                        <>
                          Enter the percentage of target energy intake from fat.
                          <br />
                          E.g., 30 for 30%.
                        </>
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              {!isMobile && (
                <Grid item md={0.5}>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      backgroundColor: theme.palette.divider,
                      width: 2,
                      mx: 2,
                    }}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={5.5}>
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Calculation Results
                  </Typography>
                  {calculatedData.leanMass !== 0 && (
                    <>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <CheckCircleIcon
                              sx={{ mr: 1, color: theme.palette.success.main }}
                            />
                            <Typography>
                              Lean Mass:{" "}
                              <strong>
                                {Number(calculatedData.leanMass).toFixed(2)} kg
                              </strong>
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <AccessTimeIcon
                              sx={{ mr: 1, color: theme.palette.info.main }}
                            />
                            <Typography>
                              BMR:{" "}
                              <strong>
                                {Number(calculatedData.bmr).toFixed(2)} kcal/day
                              </strong>
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <FitnessCenterIcon
                              sx={{ mr: 1, color: theme.palette.primary.main }}
                            />
                            <Typography>
                              Training EE:{" "}
                              <strong>
                                {Number(calculatedData.trainingEE).toFixed(2)}{" "}
                                kcal
                              </strong>
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <SelfImprovementIcon
                              sx={{ mr: 1, color: theme.palette.warning.main }}
                            />
                            <Typography>
                              Resting EE:{" "}
                              <strong>
                                {Number(calculatedData.REE).toFixed(2)} kcal
                              </strong>
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <EqualizerIcon
                              sx={{ mr: 1, color: theme.palette.error.main }}
                            />
                            <Typography>
                              Total EE:{" "}
                              <strong>
                                {Number(calculatedData.totalEE).toFixed(2)} kcal
                              </strong>
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <LocalDiningIcon
                              sx={{ mr: 1, color: theme.palette.success.main }}
                            />
                            <Typography>
                              Maintenance Intake:{" "}
                              <strong>
                                {Number(
                                  calculatedData.maintenanceIntake
                                ).toFixed(2)}{" "}
                                kcal
                              </strong>
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <TrendingUpIcon
                              sx={{ mr: 1, color: theme.palette.primary.main }}
                            />
                            <Typography>
                              Target Intake:{" "}
                              <strong>
                                {Number(calculatedData.targetIntake).toFixed(2)}{" "}
                                kcal
                              </strong>
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Divider
                        sx={{
                          my: 3,
                          backgroundColor: theme.palette.divider,
                          height: 2,
                        }}
                      />

                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: "bold", mb: 1 }}
                            >
                              Macro Breakdown
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <ArrowForwardIcon
                                sx={{
                                  mr: 1,
                                  color: theme.palette.primary.main,
                                }}
                              />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: "bold" }}
                              >
                                Protein:
                              </Typography>
                              <Typography sx={{ ml: 1 }}>
                                {Number(calculatedData.proteinGrams).toFixed(2)}{" "}
                                g (
                                {Number(calculatedData.proteinKcal).toFixed(2)}{" "}
                                kcal)
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <ArrowForwardIcon
                                sx={{ mr: 1, color: theme.palette.error.main }}
                              />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: "bold" }}
                              >
                                Fat:
                              </Typography>
                              <Typography sx={{ ml: 1 }}>
                                {Number(calculatedData.fatGrams).toFixed(2)} g (
                                {Number(calculatedData.fatKcal).toFixed(2)}{" "}
                                kcal)
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <ArrowForwardIcon
                                sx={{
                                  mr: 1,
                                  color: theme.palette.success.main,
                                }}
                              />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: "bold" }}
                              >
                                Carbs:
                              </Typography>
                              <Typography sx={{ ml: 1 }}>
                                {Number(calculatedData.carbsGrams).toFixed(2)} g
                                ({Number(calculatedData.carbsKcal).toFixed(2)}{" "}
                                kcal)
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ height: 250 }}>
                            <MacroPieChart
                              targetIntake={calculatedData.targetIntake}
                              protein={calculatedData.proteinGrams}
                              fat={calculatedData.fatGrams}
                              carbs={calculatedData.carbsGrams}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
            <Box mt={2}>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save Nutrition Program
              </Button>
            </Box>
          </Paper>
        </Box>
      </Section>
    </MainContainer>
  );
};

export default NutritionCalculatorForm;
